import React from "react";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import moment from "moment";
import { registerKokilaFont } from "./registerKokilaFont";
import { ReportPdfDocument } from "../Pages/Reports/ReportDocuments/ReportPdfDocument";

export function getDefaultReportFooter() {
  return `Generated At ${moment().format(
    "DD MMM YYYY"
  )} From Criminal Monitoring Intelligent System`;
}

export function getReportHeaderText({ user, cityName, districtName }) {
  if (user?.role === "2" && districtName) {
    return `${districtName} (${cityName || ""})`;
  }
  return cityName || "";
}

export function getReportDateRangeText(startDate, endDate) {
  const format = "DD-MM-YYYY";
  const start =
    startDate && moment(startDate).isValid()
      ? moment(startDate).format(format)
      : null;
  const end =
    endDate && moment(endDate).isValid()
      ? moment(endDate).format(format)
      : null;

  if (start && end) {
    return `From: ${start} To ${end}`;
  }
  if (start) {
    return `From: ${start}`;
  }
  if (end) {
    return `To ${end}`;
  }
  return "";
}

const isSerialColumn = (key) =>
  /^(sr\.?\s*no|अ\.?\s*क्र)/i.test(String(key).trim());

export function sheetDataToPdfColumns(arrData) {
  if (!arrData?.length) {
    return [];
  }

  const keys = Object.keys(arrData[0]);
  const weights = keys.map((key) => {
    const headerLen = String(key).length;
    const dataLen = Math.max(
      ...arrData.map((row) => String(row[key] ?? "").length),
      0
    );
    const contentLen = Math.max(headerLen, dataLen);

    if (isSerialColumn(key)) {
      return Math.max(4, Math.min(contentLen, 6));
    }
    if (contentLen > 40) {
      return Math.min(55, contentLen);
    }
    if (contentLen > 20) {
      return Math.min(30, contentLen);
    }
    return Math.max(8, Math.min(contentLen, 18));
  });

  const totalWeight = weights.reduce((sum, w) => sum + w, 0) || 1;

  const widthPercents = weights.map((w) => (w / totalWeight) * 100);
  const normalizedWidths = widthPercents.map((w, i, arr) => {
    if (i === arr.length - 1) {
      const used = arr.slice(0, -1).reduce((sum, v) => sum + v, 0);
      return Math.max(4, 100 - used);
    }
    return Math.round(w * 100) / 100;
  });

  return keys.map((key, index) => {
    const longText = weights[index] >= 25;

    return {
      key,
      label: key,
      widthPercent: normalizedWidths[index],
      align: isSerialColumn(key) ? "center" : longText ? "left" : "center",
    };
  });
}

export async function downloadReportPdf({
  headerText,
  reportTitle,
  dateRangeText,
  arrData,
  fileName,
  footerText = getDefaultReportFooter(),
  orientation,
}) {
  console.log("headerText",headerText)
  if (!arrData?.length) {
    throw new Error("No data available for PDF export");
  }

  const columns = sheetDataToPdfColumns(arrData);
  const resolvedOrientation =
    orientation || (columns.length > 5 ? "landscape" : "portrait");

  await registerKokilaFont();

  const blob = await pdf(
    <ReportPdfDocument
      headerText={headerText}
      reportTitle={reportTitle}
      dateRangeText={dateRangeText}
      columns={columns}
      rows={arrData}
      footerText={footerText}
      orientation={resolvedOrientation}
    />
  ).toBlob();

  const resolvedName = fileName.endsWith(".pdf") ? fileName : `${fileName}.pdf`;
  saveAs(blob, resolvedName);
  return blob;
}
